import React from 'react';

import { observer } from 'mobx-react';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';

import { useStore } from 'utils/hooks/store';
import { useNavBarHeight } from 'utils/hooks/navbar';
import routes from 'core/routes';

import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

import {
  Container,
  Header,
  SettingsDescription,
  SettingsName,
  SettingsRecap,
  SettingsRow,
  SubOptionRow,
} from './styles';

import NotificationSettingsSwitch from './components/NotificationSettingsSwitch';

const ProfileNotificationsSettings = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const {
    session: { user },
    notificationSettings: {
      loadData,
      notificationsSettings,
      isLoading,
      isFailure,
      isMentionsEmailSettingEnabled,
      updateMentionsSettings,
      isNewAssignmentsEmailSettingsEnabled,
      updateNewAssignmentsSettings,
      isNewAuditsEmailSettingEnabled,
      updateNewAuditsSettings,
      isNewUpdatesSettingsEnabled,
      updateNewUpdatesSettings,
      isEventCollaboratedEmailSettingEnabled,
      isEventCommentEmailSettingEnabled,
      isEventStageChangedEmailSettingEnabled,
      updateEventCollaboratedSettings,
      updateEventCommentSettings,
      updateEventStageChangedSettings,
      showNewUpdatesSubOptions,
      setShowNewUpdatesSubOptions,
      isNewEventsReportedEmailSettingEnabled,
      updateNewEventsReportedSettings,
      isActionUpdatesEmailSettingEnabled,
      updateActionUpdatesSettings,
    },
  } = useStore();

  React.useEffect(() => {
    if (!isLoading && !isFailure && notificationsSettings === undefined) {
      loadData();
    }
  }, [isLoading, isFailure, notificationsSettings]);

  const showNewAuditsSettings = user?.licenses.workflows && ADMIN_AND_MANAGERS.includes(user.role);

  const showNewUpdatesSettings = user?.licenses.events && ADMIN_AND_MANAGERS.includes(user.role);
  const showNewEventsReportedSettings = user?.licenses.events && ADMIN_AND_MANAGERS.includes(user.role);

  return (
    <Record
      Header={
        <MainHeader
          title="Notifications"
          breadcrumbs={[
            {
              label: 'Personal',
              link: '',
            },
            {
              label: 'Notifications',
              link: routes.dashboard.settings.notifications.toString(),
            },
          ]}
        />
      }
      Content={() => (
        <Container>
          <Header>
            <h1>Email Preferences</h1>
            <p>Manage your preferences for receiving email notifications</p>
          </Header>
          <SettingsRow>
            <NotificationSettingsSwitch
              value={isNewAssignmentsEmailSettingsEnabled}
              onChange={(checked) => {
                updateNewAssignmentsSettings({ email: checked });
              }}
            />
            <SettingsRecap>
              <SettingsName>New assignment</SettingsName>
              <SettingsDescription>
                You are assigned as the owner of an incident or an action is assigned to you
              </SettingsDescription>
            </SettingsRecap>
          </SettingsRow>

          <SettingsRow>
            <NotificationSettingsSwitch
              value={isMentionsEmailSettingEnabled}
              onChange={(checked) => {
                updateMentionsSettings({ email: checked });
              }}
              aria-label="Mentions notification switch"
            />
            <SettingsRecap>
              <SettingsName>Mentions</SettingsName>
              <SettingsDescription>New mentions on specific action or incident records</SettingsDescription>
            </SettingsRecap>
          </SettingsRow>

          <SettingsRow>
            <NotificationSettingsSwitch
              value={isActionUpdatesEmailSettingEnabled}
              onChange={(checked) => {
                updateActionUpdatesSettings({ email: checked });
              }}
              aria-label="Action updates notification switch"
            />
            <SettingsRecap>
              <SettingsName>Action updates</SettingsName>
              <SettingsDescription>Status changes and new comments on actions assigned to you</SettingsDescription>
            </SettingsRecap>
          </SettingsRow>

          {showNewAuditsSettings ? (
            <SettingsRow>
              <NotificationSettingsSwitch
                value={isNewAuditsEmailSettingEnabled}
                onChange={(checked) => {
                  updateNewAuditsSettings({ email: checked });
                }}
                aria-label="New audits notification switch"
              />
              <SettingsRecap>
                <SettingsName>New audits</SettingsName>
                <SettingsDescription>New audits completed by the organisation</SettingsDescription>
              </SettingsRecap>
            </SettingsRow>
          ) : null}

          {showNewEventsReportedSettings ? (
            <SettingsRow>
              <NotificationSettingsSwitch
                value={isNewEventsReportedEmailSettingEnabled}
                onChange={(checked) => {
                  updateNewEventsReportedSettings({ email: checked });
                }}
                aria-label="New events reported notification switch"
              />
              <SettingsRecap>
                <SettingsName>New events reported</SettingsName>
                <SettingsDescription>New events reported by the organisation</SettingsDescription>
              </SettingsRecap>
            </SettingsRow>
          ) : null}

          {showNewUpdatesSettings ? (
            <SettingsRow>
              <NotificationSettingsSwitch
                value={isNewUpdatesSettingsEnabled || showNewUpdatesSubOptions}
                onChange={(checked) => {
                  setShowNewUpdatesSubOptions(checked);
                  updateNewUpdatesSettings({ email: checked });
                }}
                aria-label="New updates notification switch"
              />
              <SettingsRecap>
                <SettingsName>New updates</SettingsName>
                <SettingsDescription>Updates to events you&apos;re added to as collaborator</SettingsDescription>
                {showNewUpdatesSubOptions && (
                  <>
                    <SubOptionRow>
                      <NotificationSettingsSwitch
                        value={isEventCollaboratedEmailSettingEnabled}
                        onChange={(checked) => {
                          updateEventCollaboratedSettings({ email: checked });
                        }}
                        aria-label="When added as a collaborator"
                      />
                      <SettingsName>When added as a collaborator</SettingsName>
                    </SubOptionRow>
                    <SubOptionRow>
                      <NotificationSettingsSwitch
                        value={isEventStageChangedEmailSettingEnabled}
                        onChange={(checked) => {
                          updateEventStageChangedSettings({ email: checked });
                        }}
                        aria-label="When the stage is updated"
                      />
                      <SettingsName>When the stage is updated</SettingsName>
                    </SubOptionRow>
                    <SubOptionRow>
                      <NotificationSettingsSwitch
                        value={isEventCommentEmailSettingEnabled}
                        onChange={(checked) => {
                          updateEventCommentSettings({ email: checked });
                        }}
                        aria-label="New comments in the activity feed"
                      />
                      <SettingsName>New comments in the activity feed</SettingsName>
                    </SubOptionRow>
                  </>
                )}
              </SettingsRecap>
            </SettingsRow>
          ) : null}
        </Container>
      )}
      navBarHeight={navBarHeight}
    />
  );
};

export default observer(ProfileNotificationsSettings);
